import {
	AdminActionGroup,
	AdminAssignment,
	useGetAdminActionQueueQuery,
	useGetShippingWeekQuery,
} from '@codezo/graphql';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ActionQueue } from '../../components/dashboard/ActionQueue';
import { dueLabel } from '../../components/dashboard/actionQueueFormat';
import { ActivityFeed } from '../../components/dashboard/ActivityFeed';
import { KpiStrip } from '../../components/dashboard/KpiStrip';
import { money } from '../../components/dashboard/performance/performanceFormat';
import { ShippingWeek } from '../../components/dashboard/ShippingWeek';
import { ThisMonthCard } from '../../components/dashboard/ThisMonthCard';
import { PerformanceTab } from '../../components/dashboard/performance/PerformanceTab';
import { ProductionTab } from '../../components/dashboard/production/ProductionTab';
import { CreateOrderQuoteModal } from '../../components/quotes/modals/CreateOrderQuoteModal';
import { Button } from '../../components/ui/button';
import { Tabs } from '../../components/ui/tabs';
import { useAuth } from '../../hooks/useAuth';
import { useFinancialAccess } from '../../hooks/useFinancialAccess';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { cn } from '../../lib/utils';
import { useCurrencyStore } from '../../stores/currency';

const POLL_MS = 60_000;

function greeting(): string {
	const h = new Date().getHours();
	if (h < 12) return 'Good morning';
	if (h < 18) return 'Good afternoon';
	return 'Good evening';
}

function scrollToQueue() {
	document.getElementById('queue-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ════════════════════════ OVERVIEW ════════════════════════ */

export type AssignmentCounts = { everyone: number; mine: number; unassigned: number };

const ASSIGNMENT_BY_PARAM: Record<string, AdminAssignment> = {
	everyone: AdminAssignment.Everyone,
	mine: AdminAssignment.Mine,
	me: AdminAssignment.Mine, // links shared before the switch was renamed
	unassigned: AdminAssignment.Unassigned,
};

type QueueResult = ReturnType<typeof useGetAdminActionQueueQuery>;

function OverviewTab({
	assignment,
	queueQuery,
	onOpenPerformance,
}: {
	assignment: AdminAssignment;
	queueQuery: QueueResult;
	/** Absent when the admin may not see financials — the This month card is left out. */
	onOpenPerformance?: () => void;
}) {
	const { data, loading, error, refetch } = queueQuery;
	const queue = data?.getAdminActionQueue;
	const items = useMemo(() => queue?.items ?? [], [queue]);
	const counts = queue?.counts;
	const cur = useCurrencyStore((st) => st.selectedCurrency);

	const studioToday = items.filter(
		(i) => i.group === AdminActionGroup.StudioAction && dueLabel(i).text === 'Today'
	).length;
	const waiting = items.filter((i) => i.group === AdminActionGroup.WaitingOnClient);
	const oldestWait = waiting.reduce<number>((max, i) => {
		if (!i.waitingSince) return max;
		const days = Math.floor((Date.now() - new Date(i.waitingSince).getTime()) / 86_400_000);
		return Math.max(max, days);
	}, 0);
	const weekQuery = useGetShippingWeekQuery({
		variables: { assignment },
		fetchPolicy: 'no-cache',
		pollInterval: POLL_MS,
	});
	const week = weekQuery.data?.getShippingWeek;

	return (
		<div className="flex flex-col gap-4">
			<KpiStrip
				loading={loading && !queue}
				cells={[
					{
						label: 'Overdue or at risk',
						value: counts?.overdueOrAtRisk ?? 0,
						valueTone: counts?.overdueOrAtRisk ? 'danger' : undefined,
						sub: 'Production, shipments, deadlines',
						onClick: scrollToQueue,
					},
					{
						label: 'Needs studio action',
						value: counts?.studioAction ?? 0,
						sub: (
							<>
								<span className={studioToday ? 'text-status-warn' : undefined}>
									{studioToday} due today
								</span>
								{' · '}the rest by priority
							</>
						),
						onClick: scrollToQueue,
					},
					{
						label: 'Waiting on client',
						value: counts?.waitingOnClient ?? 0,
						sub: waiting.length ? (
							<>
								oldest{' '}
								<span
									className={
										oldestWait >= 7
											? 'text-status-danger'
											: oldestWait >= 3
												? 'text-status-warn'
												: undefined
									}
								>
									{oldestWait} {oldestWait === 1 ? 'day' : 'days'}
								</span>
								{counts?.waitingOnClientAmount ? (
									<> · {money(counts.waitingOnClientAmount, cur)}</>
								) : null}
							</>
						) : (
							'Nothing outstanding'
						),
						onClick: scrollToQueue,
					},
					{
						label: 'Shipping this week',
						value: week?.total ?? 0,
						sub: week ? (
							<>
								<span className={week.missed ? 'text-status-danger' : undefined}>
									{week.missed} missed
								</span>
								{' · '}
								{week.express} express
							</>
						) : undefined,
						onClick: () =>
							document
								.getElementById('shipping-week')
								?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
					},
				]}
			/>

			<ActionQueue
				items={items}
				loading={loading}
				error={!!error}
				onRetry={() => refetch()}
				onChanged={() => refetch()}
				generatedAt={queue?.generatedAt}
				emptyText={
					assignment === AdminAssignment.Mine
						? 'Nothing assigned to you needs a reaction. Work is yours when you are an assigned admin on its quote.'
						: assignment === AdminAssignment.Unassigned
							? 'Every open item has an assigned admin.'
							: undefined
				}
			/>

			<ShippingWeek week={week} loading={weekQuery.loading} />

			{/* As in the design: what happened, beside how the month is going. */}
			<div
				className={cn(
					'grid items-start gap-4',
					onOpenPerformance && 'lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]'
				)}
			>
				<ActivityFeed assignment={assignment} />
				{onOpenPerformance && <ThisMonthCard onOpenPerformance={onOpenPerformance} />}
			</div>
		</div>
	);
}

/* ════════════════════════ PAGE ════════════════════════ */

type Tab = 'overview' | 'production' | 'performance';

export function DashboardPage() {
	const { user } = useAuth();
	useSetPageMeta('Dashboard', [{ label: 'Dashboard' }]);
	const [tabParam, setTab] = useQueryParam('tab', 'overview');
	// Financials (Performance, This month) only for admins with access; a shared link to the
	// Performance tab lands on the Overview for everyone else. The backend refuses the data anyway.
	const { canViewFinancials } = useFinancialAccess();
	const tab = tabParam === 'performance' && !canViewFinancials ? 'overview' : tabParam;
	const [owner, setOwner] = useQueryParam('owner', 'everyone');
	const [createOpen, setCreateOpen] = useState(false);
	const [productionCounts, setProductionCounts] = useState<AssignmentCounts | null>(null);
	const assignment = ASSIGNMENT_BY_PARAM[owner] ?? AdminAssignment.Everyone;
	const { selectedCurrency } = useCurrencyStore();

	// The queue feeds the header (summary line, tab counts) on every tab, so it lives on the page.
	const queueQuery = useGetAdminActionQueueQuery({
		variables: { assignment, currency: selectedCurrency },
		fetchPolicy: 'no-cache',
		pollInterval: POLL_MS,
		notifyOnNetworkStatusChange: false,
	});
	const { refetch: refetchQueue } = queueQuery;
	// Refresh when the admin comes back to the tab; polling alone can leave it a minute stale.
	useEffect(() => {
		const onFocus = () => {
			if (document.visibilityState === 'visible') refetchQueue();
		};
		document.addEventListener('visibilitychange', onFocus);
		return () => document.removeEventListener('visibilitychange', onFocus);
	}, [refetchQueue]);
	const queueCounts = queueQuery.data?.getAdminActionQueue.counts;
	const counts: AssignmentCounts | null =
		tab === 'production'
			? productionCounts
			: queueCounts
				? {
						everyone: queueCounts.everyone,
						mine: queueCounts.mine,
						unassigned: queueCounts.unassigned,
					}
				: null;
	const ownerKey = owner === 'me' ? 'mine' : owner;

	const handle = user?.email?.split('@')[0];
	const userName = handle ? handle.charAt(0).toUpperCase() + handle.slice(1) : 'there';
	const now = new Date();
	// "Thursday, 22 October", as in the design.
	const today = `${now.toLocaleDateString('en-GB', { weekday: 'long' })}, ${now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`;

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<div className="mb-5 flex flex-wrap items-end gap-x-5 gap-y-3">
				<div className="flex min-w-0 flex-1 basis-full flex-col gap-1 sm:basis-auto">
					<h1 className="text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1">
						{greeting()}, {userName}
					</h1>
					<p className="text-sm text-text-3">
						{today}
						{queueCounts && (
							<>
								{' · '}
								<span
									className={
										queueCounts.overdueOrAtRisk
											? 'text-status-danger'
											: undefined
									}
								>
									{queueCounts.overdueOrAtRisk} overdue or at risk
								</span>
								{' · '}
								{queueCounts.studioAction} need studio action
								{' · '}
								{queueCounts.waitingOnClient} waiting on clients
							</>
						)}
					</p>
				</div>
				<div
					role="group"
					aria-label="Show work for"
					// Performance is the whole studio's numbers; whose work it is doesn't apply there.
					className={cn(
						'inline-flex rounded-lg border border-line-1 bg-surface p-0.5',
						tab === 'performance' && 'hidden'
					)}
				>
					{(
						[
							['everyone', 'Everyone'],
							['mine', 'Mine'],
							['unassigned', 'Unassigned'],
						] as const
					).map(([value, label]) => (
						<button
							key={value}
							type="button"
							aria-pressed={ownerKey === value}
							onClick={() => setOwner(value)}
							title={
								value === 'mine'
									? 'Work on quotes you are assigned to'
									: value === 'unassigned'
										? 'Work on quotes with no assigned admin'
										: undefined
							}
							className={cn(
								'inline-flex h-6 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors',
								ownerKey === value
									? 'bg-surface-fill text-text-1 shadow-[0_0_0_1px_var(--color-line-2)]'
									: 'text-text-3 hover:text-text-1'
							)}
						>
							{label}
							{counts && (
								<span className="tabular-nums text-text-4">{counts[value]}</span>
							)}
						</button>
					))}
				</div>
				<Button onClick={() => setCreateOpen(true)}>
					<Plus /> New quote
				</Button>
			</div>

			<div className="mb-5">
				<Tabs
					value={tab}
					onChange={(v) => setTab(v as Tab)}
					items={[
						{
							value: 'overview',
							label: 'Overview',
							count: queueCounts?.total,
							tone: queueCounts?.overdueOrAtRisk ? 'danger' : undefined,
						},
						{
							value: 'production',
							label: 'Production',
							count: queueCounts?.liveOrders,
						},
						...(canViewFinancials
							? [{ value: 'performance', label: 'Performance' }]
							: []),
					]}
				/>
			</div>

			{tab === 'performance' && <PerformanceTab />}
			{tab === 'production' && (
				<ProductionTab assignment={assignment} onCounts={setProductionCounts} />
			)}
			{tab !== 'performance' && tab !== 'production' && (
				<OverviewTab
					assignment={assignment}
					queueQuery={queueQuery}
					onOpenPerformance={canViewFinancials ? () => setTab('performance') : undefined}
				/>
			)}

			<CreateOrderQuoteModal open={createOpen} onOpenChange={setCreateOpen} />
		</div>
	);
}
